/**
 * DSL de fórmulas para formularios QMS.
 *
 * Subset acotado y seguro (sin `eval`, sin `new Function`, sin acceso al
 * scope global). Soporta:
 *
 *   - Aritmética: + - * / % con paréntesis y precedencia estándar.
 *   - Comparaciones: == != < <= > >= y operadores lógicos `&&`, `||`, `!`.
 *   - Referencias a campos del mismo formulario: `field('id')` o el azúcar `${id}`.
 *   - Funciones: IF, MIN, MAX, SUM, AVG, ROUND, ABS, CONCAT, LEN, NOW, TODAY,
 *     DAYS, IFEMPTY, COALESCE, LOOKUP.
 *   - Literales: números, strings entre comillas dobles o simples, booleanos
 *     `true`/`false`, `null`.
 *
 * Seguridad (alineado con reglas Sonar S5852/S2076):
 *   - Input acotado a 2000 caracteres (constante MAX_INPUT).
 *   - Sin acceso a globals, sin require, sin `process`.
 *   - Lookups externos se delegan a un callback inyectado por el caller; sin
 *     llamadas implícitas a red ni a disco.
 *   - Profundidad de recursión acotada a 64 niveles.
 *   - Cuenta de tokens y nodos acotada a 5000 para evitar DoS por payload.
 */

export const MAX_INPUT_LEN = 2000;
export const MAX_NODES = 5000;
export const MAX_DEPTH = 64;

export type ScalarValue = string | number | boolean | Date | null;

/** Contexto de evaluación: valores actuales del formulario + helpers externos. */
export interface FormulaContext {
  /** Resolver de `field('id')` y `${id}` con el valor actual del formulario. */
  getField: (fieldId: string) => ScalarValue | undefined;
  /** Resolver de LOOKUP. Si no se inyecta, LOOKUP devuelve null. */
  lookup?: (templateCode: string, key: ScalarValue, returnField: string) => ScalarValue | undefined;
  /** Fecha “now” inyectable para tests deterministas. Por defecto `new Date()`. */
  now?: () => Date;
}

export interface EvaluationResult {
  value: ScalarValue;
  /** Errores no fatales encontrados (referencias rotas, divisiones, etc.). */
  warnings: string[];
}

/** Error lanzado por el parser cuando la sintaxis es inválida o supera límites. */
export class FormulaSyntaxError extends Error {
  constructor(message: string, public readonly position?: number) {
    super(message);
    this.name = 'FormulaSyntaxError';
  }
}

// ----------------------------- Tokenizer -----------------------------

type TokenType =
  | 'number'
  | 'string'
  | 'ident'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'op'
  | 'dollar_ref'
  | 'eof';

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

const OPERATORS = ['==', '!=', '<=', '>=', '&&', '||', '<', '>', '+', '-', '*', '/', '%', '!'];

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}
function isAlpha(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_';
}
function isAlphaNum(ch: string): boolean {
  return isAlpha(ch) || isDigit(ch);
}

function tokenize(input: string): Token[] {
  if (input.length > MAX_INPUT_LEN) {
    throw new FormulaSyntaxError(`Fórmula excede ${MAX_INPUT_LEN} caracteres`);
  }
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    const ch = input[i];

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // ${ident}
    if (ch === '$' && input[i + 1] === '{') {
      const close = input.indexOf('}', i + 2);
      if (close === -1) throw new FormulaSyntaxError('Falta cierre en ${...}', i);
      const ident = input.slice(i + 2, close);
      if (!/^[A-Za-z_][A-Za-z0-9_-]{0,63}$/.test(ident)) {
        throw new FormulaSyntaxError(`Identificador inválido en \${${ident}}`, i);
      }
      tokens.push({ type: 'dollar_ref', value: ident, pos: i });
      i = close + 1;
      continue;
    }

    if (isDigit(ch) || (ch === '.' && isDigit(input[i + 1] ?? ''))) {
      let j = i;
      while (j < input.length && (isDigit(input[j]!) || input[j] === '.')) j++;
      tokens.push({ type: 'number', value: input.slice(i, j), pos: i });
      i = j;
      continue;
    }

    if (ch === '"' || ch === "'") {
      const close = input.indexOf(ch, i + 1);
      if (close === -1) throw new FormulaSyntaxError('String sin cierre', i);
      tokens.push({ type: 'string', value: input.slice(i + 1, close), pos: i });
      i = close + 1;
      continue;
    }

    if (isAlpha(ch)) {
      let j = i;
      while (j < input.length && isAlphaNum(input[j]!)) j++;
      tokens.push({ type: 'ident', value: input.slice(i, j), pos: i });
      i = j;
      continue;
    }

    if (ch === '(') { tokens.push({ type: 'lparen', value: '(', pos: i }); i++; continue; }
    if (ch === ')') { tokens.push({ type: 'rparen', value: ')', pos: i }); i++; continue; }
    if (ch === ',') { tokens.push({ type: 'comma', value: ',', pos: i }); i++; continue; }

    const two = input.slice(i, i + 2);
    if (OPERATORS.includes(two)) { tokens.push({ type: 'op', value: two, pos: i }); i += 2; continue; }
    if (OPERATORS.includes(ch)) { tokens.push({ type: 'op', value: ch, pos: i }); i++; continue; }

    throw new FormulaSyntaxError(`Caracter inesperado '${ch}'`, i);
  }

  if (tokens.length > MAX_NODES) {
    throw new FormulaSyntaxError(`Demasiados tokens (${tokens.length} > ${MAX_NODES})`);
  }

  tokens.push({ type: 'eof', value: '', pos: input.length });
  return tokens;
}

// ----------------------------- Parser (recursive descent) -----------------------------

type AstNode =
  | { kind: 'literal'; value: ScalarValue }
  | { kind: 'ref'; id: string }
  | { kind: 'call'; name: string; args: AstNode[] }
  | { kind: 'binary'; op: string; left: AstNode; right: AstNode }
  | { kind: 'unary'; op: string; operand: AstNode };

class Parser {
  private pos = 0;
  private nodeCount = 0;

  constructor(private tokens: Token[]) {}

  parseExpression(depth = 0): AstNode {
    if (depth > MAX_DEPTH) throw new FormulaSyntaxError('Recursión excede límite');
    return this.parseLogicalOr(depth);
  }

  private peek(): Token {
    return this.tokens[this.pos]!;
  }
  private consume(): Token {
    return this.tokens[this.pos++]!;
  }
  private expect(type: TokenType, value?: string): Token {
    const t = this.peek();
    if (t.type !== type || (value !== undefined && t.value !== value)) {
      throw new FormulaSyntaxError(`Esperado ${type}${value ? `(${value})` : ''}, obtenido '${t.value}'`, t.pos);
    }
    return this.consume();
  }
  private node<T extends AstNode>(n: T): T {
    this.nodeCount++;
    if (this.nodeCount > MAX_NODES) throw new FormulaSyntaxError('AST excede tamaño máximo');
    return n;
  }

  private parseLogicalOr(depth: number): AstNode {
    let left = this.parseLogicalAnd(depth);
    while (this.peek().type === 'op' && this.peek().value === '||') {
      this.consume();
      const right = this.parseLogicalAnd(depth + 1);
      left = this.node({ kind: 'binary', op: '||', left, right });
    }
    return left;
  }
  private parseLogicalAnd(depth: number): AstNode {
    let left = this.parseEquality(depth);
    while (this.peek().type === 'op' && this.peek().value === '&&') {
      this.consume();
      const right = this.parseEquality(depth + 1);
      left = this.node({ kind: 'binary', op: '&&', left, right });
    }
    return left;
  }
  private parseEquality(depth: number): AstNode {
    let left = this.parseComparison(depth);
    while (this.peek().type === 'op' && (this.peek().value === '==' || this.peek().value === '!=')) {
      const op = this.consume().value;
      const right = this.parseComparison(depth + 1);
      left = this.node({ kind: 'binary', op, left, right });
    }
    return left;
  }
  private parseComparison(depth: number): AstNode {
    let left = this.parseAdditive(depth);
    while (this.peek().type === 'op' && ['<', '<=', '>', '>='].includes(this.peek().value)) {
      const op = this.consume().value;
      const right = this.parseAdditive(depth + 1);
      left = this.node({ kind: 'binary', op, left, right });
    }
    return left;
  }
  private parseAdditive(depth: number): AstNode {
    let left = this.parseMultiplicative(depth);
    while (this.peek().type === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume().value;
      const right = this.parseMultiplicative(depth + 1);
      left = this.node({ kind: 'binary', op, left, right });
    }
    return left;
  }
  private parseMultiplicative(depth: number): AstNode {
    let left = this.parseUnary(depth);
    while (this.peek().type === 'op' && ['*', '/', '%'].includes(this.peek().value)) {
      const op = this.consume().value;
      const right = this.parseUnary(depth + 1);
      left = this.node({ kind: 'binary', op, left, right });
    }
    return left;
  }
  private parseUnary(depth: number): AstNode {
    if (this.peek().type === 'op' && (this.peek().value === '-' || this.peek().value === '!')) {
      const op = this.consume().value;
      const operand = this.parseUnary(depth + 1);
      return this.node({ kind: 'unary', op, operand });
    }
    return this.parsePrimary(depth);
  }
  private parsePrimary(depth: number): AstNode {
    const t = this.peek();
    if (t.type === 'number') {
      this.consume();
      return this.node({ kind: 'literal', value: Number(t.value) });
    }
    if (t.type === 'string') {
      this.consume();
      return this.node({ kind: 'literal', value: t.value });
    }
    if (t.type === 'dollar_ref') {
      this.consume();
      return this.node({ kind: 'ref', id: t.value });
    }
    if (t.type === 'ident') {
      const ident = this.consume().value;
      if (ident === 'true') return this.node({ kind: 'literal', value: true });
      if (ident === 'false') return this.node({ kind: 'literal', value: false });
      if (ident === 'null') return this.node({ kind: 'literal', value: null });
      if (this.peek().type === 'lparen') {
        return this.parseCall(ident, depth);
      }
      // identifier sin '(' se considera referencia (azúcar para field('x'))
      return this.node({ kind: 'ref', id: ident });
    }
    if (t.type === 'lparen') {
      this.consume();
      const expr = this.parseExpression(depth + 1);
      this.expect('rparen');
      return expr;
    }
    throw new FormulaSyntaxError(`Token inesperado '${t.value}'`, t.pos);
  }
  private parseCall(name: string, depth: number): AstNode {
    this.expect('lparen');
    const args: AstNode[] = [];
    if (this.peek().type !== 'rparen') {
      args.push(this.parseExpression(depth + 1));
      while (this.peek().type === 'comma') {
        this.consume();
        args.push(this.parseExpression(depth + 1));
      }
    }
    this.expect('rparen');
    return this.node({ kind: 'call', name, args });
  }
}

// ----------------------------- Evaluator -----------------------------

function toNumber(v: ScalarValue): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v instanceof Date) return v.getTime();
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}
function toBoolean(v: ScalarValue): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return v !== '' && v.toLowerCase() !== 'false';
  return Boolean(v);
}
function toStringSafe(v: ScalarValue): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString();
  return String(v);
}
function isEmpty(v: ScalarValue): boolean {
  return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

const BUILTIN: Record<string, (args: ScalarValue[], ctx: FormulaContext, warnings: string[]) => ScalarValue> = {
  IF(args) {
    if (args.length !== 3) throw new FormulaSyntaxError('IF requiere 3 argumentos');
    return toBoolean(args[0]!) ? args[1]! : args[2]!;
  },
  MIN(args) { return args.length ? Math.min(...args.map(toNumber)) : 0; },
  MAX(args) { return args.length ? Math.max(...args.map(toNumber)) : 0; },
  SUM(args) { return args.reduce<number>((a, b) => a + toNumber(b), 0); },
  AVG(args) {
    if (args.length === 0) return 0;
    return args.reduce<number>((a, b) => a + toNumber(b), 0) / args.length;
  },
  ROUND(args) {
    const n = toNumber(args[0] ?? 0);
    const decimals = Math.max(0, Math.min(10, Math.trunc(toNumber(args[1] ?? 0))));
    const factor = Math.pow(10, decimals);
    return Math.round(n * factor) / factor;
  },
  ABS(args) { return Math.abs(toNumber(args[0] ?? 0)); },
  CONCAT(args) { return args.map(toStringSafe).join(''); },
  LEN(args) { return toStringSafe(args[0] ?? '').length; },
  NOW(_, ctx) { return (ctx.now ? ctx.now() : new Date()).toISOString(); },
  TODAY(_, ctx) {
    const d = ctx.now ? ctx.now() : new Date();
    const isoDate = d.toISOString().slice(0, 10);
    return isoDate;
  },
  DAYS(args) {
    const a = args[0]; const b = args[1];
    const da = a instanceof Date ? a : new Date(toStringSafe(a ?? null));
    const db = b instanceof Date ? b : new Date(toStringSafe(b ?? null));
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 0;
    return Math.round((da.getTime() - db.getTime()) / 86_400_000);
  },
  IFEMPTY(args) { return isEmpty(args[0] ?? null) ? args[1]! : args[0]!; },
  COALESCE(args) {
    for (const a of args) {
      if (!isEmpty(a)) return a;
    }
    return null;
  },
  field(args, ctx) {
    const id = toStringSafe(args[0] ?? null);
    if (!id) return null;
    const v = ctx.getField(id);
    return v ?? null;
  },
  LOOKUP(args, ctx, warnings) {
    if (!ctx.lookup) {
      warnings.push('LOOKUP: callback no provisto; devuelvo null');
      return null;
    }
    if (args.length !== 3) throw new FormulaSyntaxError('LOOKUP requiere 3 argumentos');
    const tpl = toStringSafe(args[0] ?? null).slice(0, 64);
    const key = args[1] ?? null;
    const ret = toStringSafe(args[2] ?? null).slice(0, 64);
    if (!tpl || !ret) return null;
    const v = ctx.lookup(tpl, key, ret);
    return v ?? null;
  },
};

function evaluateNode(node: AstNode, ctx: FormulaContext, warnings: string[], depth: number): ScalarValue {
  if (depth > MAX_DEPTH) throw new FormulaSyntaxError('Evaluación supera profundidad');

  switch (node.kind) {
    case 'literal':
      return node.value;
    case 'ref': {
      const v = ctx.getField(node.id);
      return v ?? null;
    }
    case 'unary': {
      const v = evaluateNode(node.operand, ctx, warnings, depth + 1);
      if (node.op === '-') return -toNumber(v);
      if (node.op === '!') return !toBoolean(v);
      throw new FormulaSyntaxError(`Operador unario desconocido ${node.op}`);
    }
    case 'binary':
      return evalBinary(node, ctx, warnings, depth);
    case 'call': {
      const fn = BUILTIN[node.name];
      if (!fn) {
        warnings.push(`Función desconocida ${node.name}`);
        return null;
      }
      const args = node.args.map((a) => evaluateNode(a, ctx, warnings, depth + 1));
      return fn(args, ctx, warnings);
    }
  }
}

function evalBinary(
  node: Extract<AstNode, { kind: 'binary' }>,
  ctx: FormulaContext,
  warnings: string[],
  depth: number,
): ScalarValue {
  const op = node.op;

  if (op === '&&') {
    const l = evaluateNode(node.left, ctx, warnings, depth + 1);
    return toBoolean(l) ? evaluateNode(node.right, ctx, warnings, depth + 1) : l;
  }
  if (op === '||') {
    const l = evaluateNode(node.left, ctx, warnings, depth + 1);
    return toBoolean(l) ? l : evaluateNode(node.right, ctx, warnings, depth + 1);
  }

  const left = evaluateNode(node.left, ctx, warnings, depth + 1);
  const right = evaluateNode(node.right, ctx, warnings, depth + 1);

  switch (op) {
    case '+': {
      if (typeof left === 'string' || typeof right === 'string') {
        return toStringSafe(left) + toStringSafe(right);
      }
      return toNumber(left) + toNumber(right);
    }
    case '-': return toNumber(left) - toNumber(right);
    case '*': return toNumber(left) * toNumber(right);
    case '/': {
      const r = toNumber(right);
      if (r === 0) { warnings.push('División por cero'); return null; }
      return toNumber(left) / r;
    }
    case '%': {
      const r = toNumber(right);
      if (r === 0) { warnings.push('Módulo por cero'); return null; }
      return toNumber(left) % r;
    }
    case '==': return Object.is(left, right) || toStringSafe(left) === toStringSafe(right);
    case '!=': return !(Object.is(left, right) || toStringSafe(left) === toStringSafe(right));
    case '<':  return toNumber(left) < toNumber(right);
    case '<=': return toNumber(left) <= toNumber(right);
    case '>':  return toNumber(left) > toNumber(right);
    case '>=': return toNumber(left) >= toNumber(right);
  }
  throw new FormulaSyntaxError(`Operador desconocido ${op}`);
}

// ----------------------------- Public API -----------------------------

function ensureBounded(expression: string): string {
  const raw = expression ?? '';
  if (raw.length > MAX_INPUT_LEN) {
    throw new FormulaSyntaxError(`Fórmula excede ${MAX_INPUT_LEN} caracteres`);
  }
  return raw;
}

export function evaluateFormula(expression: string, ctx: FormulaContext): EvaluationResult {
  const bounded = ensureBounded(expression);
  if (bounded.trim() === '') return { value: null, warnings: [] };

  const tokens = tokenize(bounded);
  const parser = new Parser(tokens);
  const ast = parser.parseExpression();

  const warnings: string[] = [];
  const value = evaluateNode(ast, ctx, warnings, 0);
  return { value, warnings };
}

/**
 * Compila una fórmula a una función reutilizable. Útil para evaluar muchas
 * respuestas reusando el mismo AST sin re-parsear.
 */
export function compileFormula(expression: string): (ctx: FormulaContext) => EvaluationResult {
  const bounded = ensureBounded(expression);
  if (bounded.trim() === '') return () => ({ value: null, warnings: [] });
  const tokens = tokenize(bounded);
  const parser = new Parser(tokens);
  const ast = parser.parseExpression();
  return (ctx: FormulaContext) => {
    const warnings: string[] = [];
    const value = evaluateNode(ast, ctx, warnings, 0);
    return { value, warnings };
  };
}

/** Helper: evalúa `visibleIf` / `requiredIf`. Resultado siempre booleano. */
export function evaluatePredicate(expression: string | undefined, ctx: FormulaContext): boolean {
  if (!expression || expression.trim() === '') return true;
  try {
    const { value } = evaluateFormula(expression, ctx);
    return toBoolean(value);
  } catch {
    return false;
  }
}
